import React from 'react';
import { Button, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { ApiConfigurationRule } from '../types';
import { formatRuleDisplay } from '../utils';

interface ConfigurationRuleViewProps {
  rule: ApiConfigurationRule;
  index: number;
  onEdit: (rule: ApiConfigurationRule) => void;
  onDelete: (ruleId: string) => void;
  isUpdating?: boolean;
  isDeleting?: boolean;
}

const ConfigurationRuleView: React.FC<ConfigurationRuleViewProps> = ({
  rule,
  index,
  onEdit,
  onDelete,
  isUpdating = false,
  isDeleting = false,
}) => {
  return (
    <div
      style={{
        marginBottom: 8,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        borderLeft: '3px solid #0046FF',
        paddingBottom: 12,
      }}
      data-test-id={`configuration-rule-${index}`}
    >
      {/* Top row: sequence number, rule text, and actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '4px 16px 0px 16px',
        }}
      >
        {/* Left side: sequence number and rule */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            {/* Sequence number - circular design like original */}
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 500,
                color: '#666',
                marginRight: '12px',
                flexShrink: 0,
              }}
            >
              {index + 1}
            </div>

            {/* Mathematical notation display */}
            <div
              style={{
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: 'monospace',
                color: '#495057',
                lineHeight: '1.5',
                wordBreak: 'break-word',
                flex: 1,
              }}
              dangerouslySetInnerHTML={{ __html: formatRuleDisplay(rule) }}
              data-test-id="configuration-rule-action"
            />
          </div>

          {/* Updated by and time info - positioned below and aligned with the rule text */}
          <div
            style={{
              marginLeft: '36px', // Align with the rule text (24px circle width + 12px margin)
              marginTop: '4px',
              fontSize: '12px',
              color: '#999',
            }}
            data-test-id="configuration-rule-updated-at"
          >
            Last Updated on{' '}
            {new Date(rule.updated_at * 1000).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}{' '}
            by {rule.created_by}
          </div>
        </div>

        {/* Right side: edit and delete actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit(rule)}
            loading={isUpdating}
            disabled={isUpdating || isDeleting}
            data-test-id="configuration-rule-edit-button"
          />
          <Popconfirm
            title="Are you sure you want to delete this configuration?"
            okText="Yes"
            cancelText="No"
            placement="topRight"
            onConfirm={() => onDelete(rule.id)}
            okButtonProps={{
              loading: isDeleting,
              danger: true,
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              disabled={isUpdating || isDeleting}
              danger
              data-test-id="configuration-rule-delete-button"
            />
          </Popconfirm>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationRuleView;
